import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, CircularProgress, IconButton, InputAdornment, LinearProgress, MenuItem, Pagination, Paper,
  Select, SelectChangeEvent, TextField, Tooltip, Typography
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import { usePermissions } from '../../../utils/usePermissions';
import { ReportSecurityWrapper } from './ReportSecurityWrapper';
import { notify } from '../../notifications';
import { apiErrorMessage } from './edit/editApi';
import { formatMonthRu, getCurrentMonthStr } from './edit/editCalc';
import { EditEmptyState } from './edit/EditCommon';
import { EXCEL_BUTTON_SX } from './summary/summaryStyles';
import {
  DirectorPharmacy, DirectorResponse, auditDirector, fetchDirector, fetchDirectorExport, loadFilters,
  recentMonths, saveFilters
} from './director/directorApi';
import { exportAllExcel, exportPharmacyExcel } from './director/directorExport';
import DirectorPharmacyBlock from './director/DirectorPharmacyBlock';

const BORDER = '1px solid #e2e8f0';
/** Пауза после последнего набранного символа поиска */
const SEARCH_DEBOUNCE_MS = 300;
const ALL_KEY = '\u0000all';

/** Месяц из запомненного выбора: только если он есть в списке, иначе текущий */
function initialFilters(months: string[]) {
  const saved = loadFilters();
  if (saved && months.indexOf(saved.month) >= 0) return { month: saved.month, search: saved.search, restored: true };
  return { month: getCurrentMonthStr(), search: '', restored: false };
}

const ReportForDirectors: React.FC = () => {
  const { hasPerm } = usePermissions();
  const canExportExcel = hasPerm('reports.view_director.excel_export');
  const canProtectCopy = hasPerm('reports.view_director.protect_copy');

  const months = useMemo(recentMonths, []);
  const initial = useMemo(() => initialFilters(months), [months]);

  const [month, setMonth] = useState(initial.month);
  const [searchInput, setSearchInput] = useState(initial.search);
  const [search, setSearch] = useState(initial.search);
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);

  const [data, setData] = useState<DirectorResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  /** Что выгружается сейчас: аптека (username) или все (ALL_KEY) */
  const [exportingKey, setExportingKey] = useState<string | null>(null);

  // Месяц, по которому запрос отчета уже записан в журнал: смена страницы, поиск и обновление не пишутся повторно.
  // После восстановления выбора автозагрузка тоже не считается новым запросом.
  const loggedMonthRef = useRef<string | null>(initial.restored ? initial.month : null);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'Отчеты подчиненных аптек | Zerde';
  }, []);

  // Поиск отправляется после паузы, а новый поиск всегда начинается с первой страницы
  useEffect(() => {
    if (searchInput === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  useEffect(() => {
    saveFilters({ month, search });
  }, [month, search]);

  useEffect(() => {
    let cancelled = false;
    const silent = loggedMonthRef.current === month;
    loggedMonthRef.current = month;

    setLoading(true);
    setError('');
    fetchDirector({ month, page, search }, silent)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (cancelled) return;
        loggedMonthRef.current = null;
        setData(null);
        setError(apiErrorMessage(err, 'Не удалось загрузить отчет'));
      })
      .then(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [month, page, search, reloadKey]);

  const handleMonthChange = (e: SelectChangeEvent<string>) => {
    setMonth(e.target.value);
    setPage(1);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    if (topRef.current) topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleRefresh = () => {
    // Ручное обновление не пишется в журнал как новый запрос отчета
    loggedMonthRef.current = month;
    setReloadKey((k) => k + 1);
  };

  const handleRetry = () => {
    loggedMonthRef.current = null;
    setReloadKey((k) => k + 1);
  };

  const busy = loading || exportingKey !== null;

  const handleExportOne = useCallback(
    async (pharmacy: DirectorPharmacy) => {
      if (!data || data.month !== month) return;
      setExportingKey(pharmacy.username);
      try {
        await exportPharmacyExcel(data.fields, pharmacy, data.month);
        auditDirector(pharmacy.username, data.month);
      } catch (err) {
        console.error('Ошибка выгрузки в Excel:', err);
        notify.error('Не удалось сформировать файл Excel');
      } finally {
        setExportingKey(null);
      }
    },
    [data, month]
  );

  const handleExportAll = async () => {
    if (exportingKey !== null) return;
    setExportingKey(ALL_KEY);
    try {
      const res = await fetchDirectorExport(month);
      if (res.pharmacies.length === 0) {
        notify.info('За выбранный месяц нет данных для выгрузки');
        return;
      }
      await exportAllExcel(res.fields, res.pharmacies, res.month);
    } catch (err) {
      console.error('Ошибка выгрузки общего отчета:', err);
      notify.error(apiErrorMessage(err, 'Не удалось сформировать файл Excel'));
    } finally {
      setExportingKey(null);
    }
  };

  const renderBody = () => {
    if (error) {
      return (
        <EditEmptyState
          icon={<ErrorOutlineIcon />}
          title="Не удалось загрузить отчет"
          text={error}
          action={<Button onClick={handleRetry} sx={{ textTransform: 'none', fontWeight: 600 }}>Повторить</Button>}
        />
      );
    }
    if (!data) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }
    if (data.total === 0) {
      return search ? (
        <EditEmptyState
          icon={<SearchOffOutlinedIcon />}
          title="Ничего не найдено"
          text="Среди подчиненных аптек нет совпадений с запросом."
        />
      ) : (
        <EditEmptyState
          icon={<StoreOutlinedIcon />}
          title="Нет подчиненных аптек"
          text="За вашей учетной записью не закреплены аптеки. Обратитесь к администратору."
        />
      );
    }
    return (
      <Box>
        {data.pharmacies.map((pharmacy) => (
          <DirectorPharmacyBlock
            key={pharmacy.username}
            pharmacy={pharmacy}
            fields={data.fields}
            canExport={canExportExcel}
            busy={busy}
            exporting={exportingKey === pharmacy.username}
            onExport={handleExportOne}
          />
        ))}
        {data.pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 2 }}>
            <Pagination
              count={data.pages}
              page={data.page}
              onChange={handlePageChange}
              disabled={loading}
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Box>
    );
  };

  // Данные прежнего месяца остаются на экране приглушенными, пока загружается новый
  const stale = loading && data !== null;

  return (
    <ReportSecurityWrapper protectCopy={canProtectCopy}>
      <Box ref={topRef} sx={{ scrollMarginTop: 16 }}>
        <Paper
          elevation={0}
          sx={{ p: 2, mb: 2, border: BORDER, borderRadius: '12px', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}
        >
          <Select value={month} onChange={handleMonthChange} size="small" sx={{ minWidth: 190 }}>
            {months.map((value) => (
              <MenuItem key={value} value={value}>{formatMonthRu(value)}</MenuItem>
            ))}
          </Select>
          <TextField
            size="small"
            placeholder="Номер, логин или ФИО"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            inputProps={{ maxLength: 50 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                </InputAdornment>
              )
            }}
            sx={{ width: 240 }}
          />
          <Tooltip title="Обновить данные" arrow>
            <span>
              <IconButton size="small" onClick={handleRefresh} disabled={busy} sx={{ color: '#64748b' }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          {data && data.total > 0 && (
            <Typography sx={{ fontSize: '0.84rem', color: '#64748b' }}>
              Аптек: <b>{data.total}</b>
            </Typography>
          )}
          {canExportExcel && (
            <Button
              variant="contained"
              onClick={handleExportAll}
              disabled={busy || !data || data.total === 0}
              startIcon={exportingKey === ALL_KEY ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
              sx={EXCEL_BUTTON_SX}
            >
              Excel общ
            </Button>
          )}
        </Paper>

        <Box sx={{ height: 2, mb: 1, mx: 0.5 }}>
          {busy && <LinearProgress sx={{ height: 2, borderRadius: 1 }} />}
        </Box>

        <Box sx={{ opacity: stale ? 0.55 : 1, transition: 'opacity 0.15s ease' }}>{renderBody()}</Box>
      </Box>
    </ReportSecurityWrapper>
  );
};

export default ReportForDirectors;
